import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Upload, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { createAttractionAllowDuplicates } from "@/lib/attractionsApi";

interface JsonTicket {
  id?: number | string;
  attraction_id?: number | string;
  title: string;
  description?: string;
  price_op_usd?: string | number;
  price_ag_usd?: string | number;
  price_cf_usd?: string | number;
  pricing_type?: string;
  cost_supplier_usd?: string | number;
  price_website_usd?: string | number;
  tax?: string | number;
}

interface JsonAttraction {
  id?: number | string;
  code: string;
  title: string;
  description?: string;
  supplier_name?: string;
  opening_hours?: string;
  tax?: string | number;
  htce_notes?: string;
  image_url?: string;
  tickets?: JsonTicket[];
}

function parseAttractionsJson(content: string): JsonAttraction[] {
  const parsed = JSON.parse(content);
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === "object") {
    const arr = parsed.attractions ?? parsed.data ?? parsed.items;
    if (Array.isArray(arr)) return arr;
  }
  throw new Error("JSON must be an array of attractions or an object with 'attractions', 'data' or 'items' array");
}

const ImportAttractions = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<{
    success: number;
    skipped: number;
    errors: string[];
    ticketsImported: number;
  } | null>(null);
  const { userRole } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const canImport = userRole === "ceo" || userRole === "general_manager";

  if (!canImport) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to import attractions.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const cleanValue = (value: string | number | null | undefined): string | null => {
    if (value == null) return null;
    const v = String(value).trim();
    if (v === "" || v === "-" || v === "?" || v.toLowerCase() === "null") {
      return null;
    }
    return v;
  };

  const parseNumber = (value: string | number | null | undefined): number | null => {
    const cleaned = cleanValue(value as any);
    if (!cleaned) return null;
    const num = Number(cleaned.replace(/,/g, ""));
    return Number.isNaN(num) ? null : num;
  };

  const parseId = (value: string | number | null | undefined): number | null => {
    const num = parseNumber(value);
    return num === null ? null : Math.trunc(num);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === "application/json") {
      setFile(selectedFile);
      setResults(null);
    } else {
      toast({
        title: "Invalid file",
        description: "Please select a valid JSON file",
        variant: "destructive",
      });
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setProgress(0);
    setResults(null);

    const errors: string[] = [];

    try {
      const fileContent = await file.text();
      const jsonData = parseAttractionsJson(fileContent);

      let success = 0;
      let skipped = 0;
      let ticketsImported = 0;

      for (let i = 0; i < jsonData.length; i++) {
        const attraction = jsonData[i];

        try {
          const code = cleanValue(attraction.code);
          if (!code) {
            errors.push(`Row ${i + 1}: missing attraction code`);
            setProgress(((i + 1) / jsonData.length) * 100);
            continue;
          }

          const tickets = (attraction.tickets || []).map((ticket) => ({
            id: parseId(ticket.id) ?? undefined,
            title: cleanValue(ticket.title) || "",
            description: cleanValue(ticket.description),
            pricing_type: cleanValue(ticket.pricing_type) || "per_person",
            cost_supplier: parseNumber(ticket.cost_supplier_usd),
            website_price: parseNumber(ticket.price_website_usd),
            price_op: parseNumber(ticket.price_op_usd),
            price_ag: parseNumber(ticket.price_ag_usd),
            price_cf: parseNumber(ticket.price_cf_usd),
            tax_percentage: parseNumber(ticket.tax),
          }));

          const payload = {
            id: parseId(attraction.id) ?? undefined,
            attraction_code: code,
            title: cleanValue(attraction.title) || "",
            description: cleanValue(attraction.description),
            supplier_name: cleanValue(attraction.supplier_name),
            opening_hours: cleanValue(attraction.opening_hours),
            tax_percentage: parseNumber(attraction.tax),
            hcte_notes: cleanValue(attraction.htce_notes),
            image_url: cleanValue(attraction.image_url),
            tickets,
          };

          await createAttractionAllowDuplicates(payload);
          success++;
          ticketsImported += tickets.length;
        } catch (err: any) {
          const message = err?.message || "Unknown error";
          if (message.toLowerCase().includes("already exists") || message.toLowerCase().includes("in use")) {
            skipped++;
          } else {
            errors.push(`Row ${i + 1}: ${message}`);
          }
        }

        setProgress(((i + 1) / jsonData.length) * 100);
      }

      setResults({ success, skipped, errors, ticketsImported });

      toast({
        title: "Import completed",
        description: `Successfully imported ${success} attractions with ${ticketsImported} tickets, skipped ${skipped}, failed ${errors.length}`,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to parse or import the JSON file";
      toast({
        title: "Import failed",
        description: message,
        variant: "destructive",
      });
      setResults({ success: 0, skipped: 0, errors: [message], ticketsImported: 0 });
    } finally {
      setImporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Import Attractions</h1>
            <p className="text-muted-foreground">Import attractions with tickets from JSON file</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/attractions")}>
            Back to Attractions
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Upload Attractions JSON</CardTitle>
            <CardDescription>
              Select a JSON file containing attractions data with nested tickets. Import will accept duplicate attraction codes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="file">JSON File</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="file"
                  type="file"
                  accept=".json"
                  onChange={handleFileChange}
                  disabled={importing}
                />
                <Upload className="h-5 w-5 text-muted-foreground" />
              </div>
              {file && <p className="text-sm text-muted-foreground">Selected: {file.name}</p>}
            </div>

            {importing && (
              <div className="space-y-2">
                <Label>Import Progress</Label>
                <Progress value={progress} />
                <p className="text-sm text-muted-foreground">{Math.round(progress)}% complete</p>
              </div>
            )}

            {results && (
              <Alert>
                <AlertDescription>
                  <div className="space-y-1">
                    <p>
                      <strong>Import Results:</strong>
                    </p>
                    <p>✓ Successfully imported: {results.success} attractions</p>
                    <p>✓ Tickets imported: {results.ticketsImported} tickets</p>
                    <p>⊘ Skipped (already exists): {results.skipped} attractions</p>
                    {results.errors.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto text-xs space-y-1">
                        {results.errors.slice(0, 20).map((error, idx) => (
                          <p key={idx} className="text-destructive">{error}</p>
                        ))}
                        {results.errors.length > 20 && (
                          <p className="text-muted-foreground italic">
                            ... and {results.errors.length - 20} more errors
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <Button onClick={handleImport} disabled={!file || importing} className="w-full">
              {importing ? "Importing..." : "Start Import"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ImportAttractions;
